import React, { useState, useEffect } from 'react';
import { User, Mail, Phone, CreditCard, Calendar, Edit2, Save, X, LogOut, Loader2, AlertCircle, Gift, Check, Shield, Clock, XCircle, FileText, Lock, Trash2, Eye, EyeOff } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { useAuth } from '../../hooks/useAuth';
import { subscriptionAPI, profileAPI, getErrorMessage } from '../../lib/api';

// Helper function to format date strings consistently (DD-MM-YYYY)
const formatDateString = (dateString) => {
  if (!dateString) return 'N/A';
  
  // If already in DD-MM-YYYY format, return as-is
  if (/^\d{2}-\d{2}-\d{4}/.test(dateString)) {
    return dateString.split(' ')[0];
  }
  
  // If in ISO format (YYYY-MM-DD or YYYY-MM-DDTHH:MM:SS), convert to DD-MM-YYYY
  if (/^\d{4}-\d{2}-\d{2}/.test(dateString)) {
    const [year, month, day] = dateString.split('T')[0].split('-');
    return `${day}-${month}-${year}`;
  }
  
  // Fallback: try to parse and format
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const Profile = () => {
  const { user, logout, updateProfile, refreshProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [subscriptionError, setSubscriptionError] = useState(null);
  const [subscriptionAccess, setSubscriptionAccess] = useState(null);
  
  // KYC State
  const [kycLoading, setKycLoading] = useState(true);
  const [kycData, setKycData] = useState(null);
  const [kycError, setKycError] = useState(null);
  const [showKycForm, setShowKycForm] = useState(false);
  const [kycSubmitting, setKycSubmitting] = useState(false);
  const [kycFormData, setKycFormData] = useState({
    pancard: '',
    dob: '',
    whatsapp: ''
  });
  const [kycFormErrors, setKycFormErrors] = useState({});
  
  // Password Change State
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordChanging, setPasswordChanging] = useState(false);
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    new_password_confirm: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false
  });
  
  // Account Deletion State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  
  const [profileData, setProfileData] = useState({
    firstName: user?.name?.split(' ')[0] || 'User',
    lastName: user?.name?.split(' ').slice(1).join(' ') || '',
    email: user?.email || 'user@example.com',
    phone: user?.whatsapp || '+91 0000000000',
    pan: user?.pancard || 'Not Provided',
    dateOfBirth: formatDateString(user?.dob) || 'Not Provided',
    joiningDate: formatDateString(user?.date_joined)
  });

  // Fetch subscription and KYC data on mount
  useEffect(() => {
    fetchSubscriptionData();
    fetchKycStatus();
  }, []);

  // Update profile data when user changes
  useEffect(() => {
    if (user) {
      setProfileData({
        firstName: user?.name?.split(' ')[0] || 'User',
        lastName: user?.name?.split(' ').slice(1).join(' ') || '',
        email: user?.email || 'user@example.com',
        phone: user?.whatsapp || '+91 0000000000',
        pan: user?.pancard || 'Not Provided',
        dateOfBirth: formatDateString(user?.dob) || 'Not Provided',
        joiningDate: formatDateString(user?.date_joined)
      });
    }
  }, [user]);

  const fetchSubscriptionData = async () => {
    setSubscriptionLoading(true);
    setSubscriptionError(null);
    try {
      const response = await subscriptionAPI.getSubscriptionAccess();
      setSubscriptionAccess(response.data.data);
    } catch (err) {
      console.error('Error fetching subscription data:', err);
      setSubscriptionError(getErrorMessage(err));
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const fetchKycStatus = async () => {
    setKycLoading(true);
    setKycError(null);
    try {
      const response = await profileAPI.getKYCStatus();
      const data = response.data.data;
      setKycData(data);
      // Pre-fill form with existing data
      setKycFormData({
        pancard: data.pancard || '',
        dob: data.dob || '',
        whatsapp: data.whatsapp || ''
      });
    } catch (err) {
      console.error('Error fetching KYC status:', err);
      setKycError(getErrorMessage(err));
    } finally {
      setKycLoading(false);
    }
  };

  const validateKycForm = () => {
    const errors = {};
    
    // PAN Card validation (Indian PAN format: ABCDE1234F)
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (!kycFormData.pancard) {
      errors.pancard = 'PAN Card is required';
    } else if (!panRegex.test(kycFormData.pancard.toUpperCase())) {
      errors.pancard = 'Invalid PAN format. Use format: ABCDE1234F';
    }
    
    // Date of Birth validation
    if (!kycFormData.dob) {
      errors.dob = 'Date of Birth is required';
    } else {
      const dobDate = new Date(kycFormData.dob);
      const today = new Date();
      const age = Math.floor((today - dobDate) / (365.25 * 24 * 60 * 60 * 1000));
      if (age < 18) {
        errors.dob = 'You must be at least 18 years old';
      } else if (age > 100) {
        errors.dob = 'Please enter a valid date of birth';
      }
    }
    
    // WhatsApp validation (optional but if provided should be valid)
    if (kycFormData.whatsapp && !/^\+?[0-9]{10,15}$/.test(kycFormData.whatsapp.replace(/\s/g, ''))) {
      errors.whatsapp = 'Invalid phone number format';
    }
    
    setKycFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleKycSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateKycForm()) {
      return;
    }
    
    setKycSubmitting(true);
    try {
      const submitData = {
        pancard: kycFormData.pancard.toUpperCase(),
        dob: kycFormData.dob,
      };
      
      // Only include whatsapp if provided
      if (kycFormData.whatsapp) {
        submitData.whatsapp = kycFormData.whatsapp;
      }
      
      await profileAPI.updateKYC(submitData);
      
      toast.success({
        title: 'KYC Submitted',
        description: 'Your KYC details have been submitted for verification.'
      });
      
      setShowKycForm(false);
      // Refresh KYC status and user profile
      await fetchKycStatus();
      if (refreshProfile) {
        await refreshProfile();
      }
    } catch (err) {
      console.error('Error submitting KYC:', err);
      const errorMessage = getErrorMessage(err);
      const fieldErrors = err.response?.data?.error?.details?.errors;
      
      if (fieldErrors) {
        setKycFormErrors(fieldErrors);
      }
      
      toast.error({
        title: 'KYC Submission Failed',
        description: errorMessage || 'Failed to submit KYC details. Please try again.'
      });
    } finally {
      setKycSubmitting(false);
    }
  };

  const handleKycFormChange = (field, value) => {
    setKycFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (kycFormErrors[field]) {
      setKycFormErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Get KYC status display info
  const getKycStatusInfo = (status, statusText) => {
    const statusMap = {
      0: { icon: Clock, color: 'yellow', bgColor: 'bg-yellow-50', textColor: 'text-yellow-700', borderColor: 'border-yellow-500' },
      1: { icon: Check, color: 'green', bgColor: 'bg-green-50', textColor: 'text-green-700', borderColor: 'border-green-500' },
      2: { icon: Clock, color: 'blue', bgColor: 'bg-blue-50', textColor: 'text-blue-700', borderColor: 'border-blue-500' },
      3: { icon: XCircle, color: 'red', bgColor: 'bg-red-50', textColor: 'text-red-700', borderColor: 'border-red-500' },
    };
    return statusMap[status] || { icon: AlertCircle, color: 'gray', bgColor: 'bg-gray-50', textColor: 'text-gray-700', borderColor: 'border-gray-500' };
  };

  // Display date as-is from backend (backend sends DD-MM-YYYY format)
  // If date is in ISO format (YYYY-MM-DD), convert it to DD-MM-YYYY
  const displayDate = (dateString) => {
    if (!dateString) return '-';
    
    // If already in DD-MM-YYYY format, return as-is
    if (/^\d{2}-\d{2}-\d{4}/.test(dateString)) {
      return dateString.split(' ')[0]; // Remove time if present
    }
    
    // If in ISO format (YYYY-MM-DD), convert to DD-MM-YYYY
    if (/^\d{4}-\d{2}-\d{2}/.test(dateString)) {
      const [year, month, day] = dateString.split('T')[0].split('-');
      return `${day}-${month}-${year}`;
    }
    
    // Fallback: try to parse and format
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Parse DD-MM-YYYY date string for calculations
  const parseDate = (dateString) => {
    if (!dateString) return null;
    
    // If in DD-MM-YYYY format
    if (/^\d{2}-\d{2}-\d{4}/.test(dateString)) {
      const [day, month, year] = dateString.split(' ')[0].split('-');
      return new Date(year, month - 1, day);
    }
    
    // If in ISO format
    return new Date(dateString);
  };

  // Get subscription details from API response
  const currentSubscription = subscriptionAccess?.subscription;
  const hasActiveSubscription = currentSubscription?.is_active;
  const freeTier = subscriptionAccess?.free_tier;
  const hasActiveFreeTier = freeTier?.is_active;

  // Calculate days remaining for free tier
  const getFreeTierDaysRemaining = () => {
    if (!freeTier?.expiry_date) return 0;
    const expiryDate = parseDate(freeTier.expiry_date);
    if (!expiryDate) return 0;
    const today = new Date();
    const diffTime = expiryDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    toast.info({
      title: 'Editing Cancelled',
      description: 'Your changes have been discarded'
    });
  };

  const handleSave = async () => {
    // Validate phone number (should be +91 followed by 10 digits starting with 6-9)
    const phoneDigits = profileData.phone.replace(/^\+91/, '');
    if (phoneDigits.length !== 10 || !/^[6-9]\d{9}$/.test(phoneDigits)) {
      toast.error({
        title: 'Invalid Phone Number',
        description: 'Please enter a valid 10-digit Indian mobile number starting with 6-9'
      });
      return;
    }
    
    // Validate name
    const fullName = `${profileData.firstName} ${profileData.lastName}`.trim();
    if (!fullName || fullName.length < 2) {
      toast.error({
        title: 'Invalid Name',
        description: 'Please enter a valid name'
      });
      return;
    }
    
    setIsSaving(true);
    try {
      // Prepare the data to update
      const updateData = {
        name: fullName,
        whatsapp: profileData.phone
      };
      
      await updateProfile(updateData);
      await refreshProfile();
      setIsEditing(false);
      toast.success({
        title: 'Profile Updated',
        description: 'Your profile has been updated successfully'
      });
    } catch (error) {
      console.error('Profile update error:', error);
      toast.error({
        title: 'Update Failed',
        description: error.message || 'Failed to update profile. Please try again.'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = async () => {
    toast.info({
      title: 'Logging Out',
      description: 'You are being signed out...'
    });
    await logout();
  };

  // Password Change Handlers
  const validatePassword = () => {
    const errors = {};
    
    if (!passwordData.old_password) {
      errors.old_password = 'Current password is required';
    }
    
    if (!passwordData.new_password) {
      errors.new_password = 'New password is required';
    } else if (passwordData.new_password.length < 8) {
      errors.new_password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(passwordData.new_password)) {
      errors.new_password = 'Password must contain uppercase, lowercase and number';
    }
    
    if (!passwordData.new_password_confirm) {
      errors.new_password_confirm = 'Please confirm your new password';
    } else if (passwordData.new_password !== passwordData.new_password_confirm) {
      errors.new_password_confirm = 'Passwords do not match';
    }
    
    if (passwordData.old_password === passwordData.new_password) {
      errors.new_password = 'New password must be different from current password';
    }
    
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (!validatePassword()) {
      return;
    }
    
    setPasswordChanging(true);
    try {
      await profileAPI.changePassword(passwordData);
      
      toast.success({
        title: 'Password Changed',
        description: 'Your password has been changed successfully. Please login with your new password.'
      });
      
      // Reset form and close
      setPasswordData({ old_password: '', new_password: '', new_password_confirm: '' });
      setShowPasswordForm(false);
      setPasswordErrors({});
      
      // Logout user after password change
      setTimeout(() => {
        logout();
      }, 2000);
      
    } catch (err) {
      console.error('Password change error:', err);
      const errorMessage = getErrorMessage(err);
      
      if (err.response?.data?.error?.code === 'INVALID_PASSWORD') {
        setPasswordErrors({ old_password: 'Current password is incorrect' });
      } else {
        toast.error({
          title: 'Password Change Failed',
          description: errorMessage || 'Failed to change password. Please try again.'
        });
      }
    } finally {
      setPasswordChanging(false);
    }
  };

  // Account Deletion Handlers
  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error({
        title: 'Confirmation Required',
        description: 'Please type DELETE to confirm account deletion'
      });
      return;
    }
    
    if (!deletePassword) {
      toast.error({
        title: 'Password Required',
        description: 'Please enter your password to confirm deletion'
      });
      return;
    }
    
    setDeleting(true);
    try {
      await profileAPI.deleteAccount({
        password: deletePassword,
        reason: deleteReason
      });
      
      toast.success({
        title: 'Account Deleted',
        description: 'Your account has been permanently deleted. We\'re sorry to see you go.'
      });
      
      // Logout user
      setTimeout(() => {
        logout();
      }, 2000);
      
    } catch (err) {
      console.error('Account deletion error:', err);
      const errorMessage = getErrorMessage(err);
      
      if (err.response?.data?.error?.code === 'INVALID_PASSWORD') {
        toast.error({
          title: 'Incorrect Password',
          description: 'The password you entered is incorrect. Please try again.'
        });
      } else {
        toast.error({
          title: 'Deletion Failed',
          description: errorMessage || 'Failed to delete account. Please try again.'
        });
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleChange = (field, value) => {
    setProfileData({ ...profileData, [field]: value });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg shadow-lg p-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div className="w-24 h-24 bg-yellow-400 rounded-full flex items-center justify-center text-gray-900 text-4xl font-bold">
              {profileData.firstName.charAt(0)}{profileData.lastName.charAt(0)}
            </div>
            <div>
              <h1 className="text-3xl font-bold mb-1">{profileData.firstName} {profileData.lastName}</h1>
              <p className="text-blue-100 flex items-center gap-2">
                <Mail size={16} />
                {profileData.email}
              </p>
              <p className="text-blue-100 flex items-center gap-2 mt-1">
                <Phone size={16} />
                {profileData.phone}
              </p>
            </div>
          </div>
          {!isEditing ? (
            <div className="flex gap-2">
              <button
                onClick={handleEdit}
                className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors flex items-center gap-2"
              >
                <Edit2 size={18} />
                Edit Profile
              </button>
              <button
                onClick={handleLogout}
                className="bg-red-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-600 transition-colors flex items-center gap-2"
              >
                <LogOut size={18} />
                Logout
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={handleSave}
                className="bg-green-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-600 transition-colors flex items-center gap-2"
              >
                <Save size={18} />
                Save
              </button>
              <button
                onClick={handleCancel}
                className="bg-gray-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-600 transition-colors flex items-center gap-2"
              >
                <X size={18} />
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Personal Information */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Personal Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">First Name</label>
              {isEditing ? (
                <input
                  type="text"
                  value={profileData.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:border-blue-500"
                />
              ) : (
                <p className="text-gray-900 font-medium bg-gray-50 px-4 py-2 rounded-lg">{profileData.firstName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name</label>
              {isEditing ? (
                <input
                  type="text"
                  value={profileData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:border-blue-500"
                />
              ) : (
                <p className="text-gray-900 font-medium bg-gray-50 px-4 py-2 rounded-lg">{profileData.lastName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
              <p className="text-gray-900 font-medium bg-gray-50 px-4 py-2 rounded-lg">{profileData.email}</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number</label>
              {isEditing ? (
                <div className="flex">
                  <span className="inline-flex items-center px-4 py-2 border-2 border-r-0 border-gray-300 rounded-l-lg bg-gray-100 text-gray-600 font-medium">
                    +91
                  </span>
                  <input
                    type="tel"
                    value={profileData.phone.replace(/^\+91/, '')}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                      handleChange('phone', `+91${value}`);
                    }}
                    placeholder="10-digit mobile number"
                    maxLength={10}
                    className="flex-1 border-2 border-gray-300 rounded-r-lg px-4 py-2 focus:outline-none focus:border-blue-500"
                  />
                </div>
              ) : (
                <p className="text-gray-900 font-medium bg-gray-50 px-4 py-2 rounded-lg">{profileData.phone}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">PAN Card</label>
              <p className="text-gray-900 font-medium bg-gray-50 px-4 py-2 rounded-lg font-mono">{profileData.pan}</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Date of Birth</label>
              <p className="text-gray-900 font-medium bg-gray-50 px-4 py-2 rounded-lg">{profileData.dateOfBirth}</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Joining Date</label>
              <p className="text-gray-900 font-medium bg-gray-50 px-4 py-2 rounded-lg flex items-center gap-2">
                <Calendar size={16} className="text-blue-600" />
                {profileData.joiningDate}
              </p>
            </div>
          </div>
          
          {/* Note about PAN and DOB */}
          <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-800">PAN Card & Date of Birth</p>
                <p className="text-sm text-amber-700 mt-1">
                  To update your PAN Card or Date of Birth, please submit your KYC details through the KYC Verification section below. 
                  If you need to correct already submitted KYC information, please contact our support team.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Subscription Info Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Subscription Details</h2>
          
          {subscriptionLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">Loading...</span>
            </div>
          ) : subscriptionError ? (
            <div className="text-center py-8">
              <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
              <p className="text-red-600 text-sm">{subscriptionError}</p>
              <button
                onClick={fetchSubscriptionData}
                className="mt-2 text-blue-600 text-sm hover:underline"
              >
                Retry
              </button>
            </div>
          ) : hasActiveSubscription ? (
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 px-4 py-3 rounded-lg text-center">
                <p className="text-sm font-medium">Current Plan</p>
                <p className="text-2xl font-bold">{currentSubscription.plan_name}</p>
              </div>

              <div className="p-4 bg-green-50 rounded-lg border-l-4 border-green-500">
                <p className="text-sm text-gray-600 mb-1">Status</p>
                <p className="text-lg font-bold text-green-600 flex items-center gap-1">
                  <Check size={18} />
                  Active
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-600 mb-1">Expiry Date</p>
                <p className="text-gray-900 font-semibold">{displayDate(currentSubscription.expiry_date)}</p>
              </div>

              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Days Remaining</p>
                <p className="text-3xl font-bold text-blue-600">{currentSubscription.days_remaining || 0}</p>
              </div>
            </div>
          ) : hasActiveFreeTier ? (
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-emerald-400 to-teal-500 text-white px-4 py-3 rounded-lg text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Gift size={18} />
                  <p className="text-sm font-medium">Free Tier Access</p>
                </div>
                <p className="text-xl font-bold">BONUS</p>
              </div>

              <div className="p-4 bg-green-50 rounded-lg border-l-4 border-green-500">
                <p className="text-sm text-gray-600 mb-1">Status</p>
                <p className="text-lg font-bold text-green-600 flex items-center gap-1">
                  <Check size={18} />
                  Active
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-600 mb-1">Expiry Date</p>
                <p className="text-gray-900 font-semibold">{displayDate(freeTier.expiry_date)}</p>
              </div>

              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Days Remaining</p>
                <p className="text-3xl font-bold text-blue-600">{getFreeTierDaysRemaining()}</p>
              </div>

              <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-xs text-yellow-700">
                  <AlertCircle size={12} className="inline mr-1" />
                  Upgrade to a premium plan for full access
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-6">
              <div className="p-4 bg-gray-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <CreditCard size={28} className="text-gray-400" />
              </div>
              <p className="text-gray-600 mb-4">No Active Subscription</p>
              <a 
                href="/pricing" 
                className="inline-block bg-yellow-400 text-gray-900 px-6 py-2 rounded-lg font-semibold hover:bg-yellow-300 transition-colors"
              >
                View Plans
              </a>
            </div>
          )}
        </div>
      </div>

      {/* KYC Status Card */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="p-6 border-b bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="text-blue-600" size={24} />
              <div>
                <h2 className="text-xl font-bold text-gray-900">KYC Verification</h2>
                <p className="text-gray-600 text-sm">Complete your KYC to access all features</p>
              </div>
            </div>
            {kycData && kycData.kyc_status !== 1 && (
              <button
                onClick={() => setShowKycForm(!showKycForm)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <FileText size={18} />
                {showKycForm ? 'Cancel' : (kycData.kyc_status === null ? 'Submit KYC' : 'Update KYC')}
              </button>
            )}
          </div>
        </div>

        <div className="p-6">
          {kycLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">Loading KYC status...</span>
            </div>
          ) : kycError ? (
            <div className="text-center py-8">
              <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
              <p className="text-red-600 text-sm">{kycError}</p>
              <button
                onClick={fetchKycStatus}
                className="mt-2 text-blue-600 text-sm hover:underline"
              >
                Retry
              </button>
            </div>
          ) : (
            <>
              {/* KYC Status Display */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {/* Status Card */}
                <div className={`p-4 rounded-lg border-l-4 ${getKycStatusInfo(kycData?.kyc_status).bgColor} ${getKycStatusInfo(kycData?.kyc_status).borderColor}`}>
                  <p className="text-sm text-gray-600 mb-1">KYC Status</p>
                  <div className={`flex items-center gap-2 font-bold ${getKycStatusInfo(kycData?.kyc_status).textColor}`}>
                    {React.createElement(getKycStatusInfo(kycData?.kyc_status).icon, { size: 18 })}
                    {kycData?.kyc_status_text || 'Not Submitted'}
                  </div>
                </div>

                {/* PAN Card */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">PAN Card</p>
                  <p className="font-bold text-gray-900 font-mono">
                    {kycData?.pancard || 'Not Provided'}
                  </p>
                </div>

                {/* Date of Birth */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Date of Birth</p>
                  <p className="font-bold text-gray-900">
                    {kycData?.dob ? displayDate(kycData.dob) : 'Not Provided'}
                  </p>
                </div>

                {/* WhatsApp */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">WhatsApp Number</p>
                  <p className="font-bold text-gray-900">
                    {kycData?.whatsapp || 'Not Provided'}
                  </p>
                </div>
              </div>

              {/* Status Messages */}
              {kycData?.kyc_status === null && (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="text-gray-500 flex-shrink-0 mt-0.5" size={18} />
                    <div>
                      <p className="text-gray-800 font-semibold text-sm">KYC Not Submitted</p>
                      <p className="text-gray-600 text-sm mt-1">
                        Please submit your KYC details to unlock all features and ensure compliance with SEBI regulations.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {kycData?.kyc_status === 0 && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Clock className="text-yellow-600 flex-shrink-0 mt-0.5" size={18} />
                    <div>
                      <p className="text-yellow-800 font-semibold text-sm">Pending Verification</p>
                      <p className="text-yellow-700 text-sm mt-1">
                        Your KYC is pending verification. Our team will review your details shortly.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {kycData?.kyc_status === 1 && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Check className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
                    <div>
                      <p className="text-green-800 font-semibold text-sm">KYC Verified</p>
                      <p className="text-green-700 text-sm mt-1">
                        Your KYC has been verified successfully. You have full access to all features.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {kycData?.kyc_status === 2 && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Clock className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                    <div>
                      <p className="text-blue-800 font-semibold text-sm">Under Review</p>
                      <p className="text-blue-700 text-sm mt-1">
                        Your KYC details are currently under review. This typically takes 1-2 business days.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {kycData?.kyc_status === 3 && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <XCircle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
                    <div>
                      <p className="text-red-800 font-semibold text-sm">KYC Rejected</p>
                      <p className="text-red-700 text-sm mt-1">
                        Your KYC verification was rejected. Please update your details and resubmit.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* KYC Form */}
              {showKycForm && (
                <div className="mt-6 p-6 bg-gray-50 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <FileText size={20} className="text-blue-600" />
                    {kycData?.kyc_status === null ? 'Submit KYC Details' : 'Update KYC Details'}
                  </h3>
                  
                  <form onSubmit={handleKycSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* PAN Card */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          PAN Card Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={kycFormData.pancard}
                          onChange={(e) => handleKycFormChange('pancard', e.target.value.toUpperCase())}
                          placeholder="ABCDE1234F"
                          maxLength={10}
                          className={`w-full border-2 rounded-lg px-4 py-2 focus:outline-none font-mono uppercase ${
                            kycFormErrors.pancard 
                              ? 'border-red-300 focus:border-red-500' 
                              : 'border-gray-300 focus:border-blue-500'
                          }`}
                        />
                        {kycFormErrors.pancard && (
                          <p className="text-red-500 text-xs mt-1">{kycFormErrors.pancard}</p>
                        )}
                        <p className="text-gray-500 text-xs mt-1">Format: 5 letters + 4 digits + 1 letter (e.g., ABCDE1234F)</p>
                      </div>

                      {/* Date of Birth */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Date of Birth <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          value={kycFormData.dob}
                          onChange={(e) => handleKycFormChange('dob', e.target.value)}
                          max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                          className={`w-full border-2 rounded-lg px-4 py-2 focus:outline-none ${
                            kycFormErrors.dob 
                              ? 'border-red-300 focus:border-red-500' 
                              : 'border-gray-300 focus:border-blue-500'
                          }`}
                        />
                        {kycFormErrors.dob && (
                          <p className="text-red-500 text-xs mt-1">{kycFormErrors.dob}</p>
                        )}
                        <p className="text-gray-500 text-xs mt-1">You must be at least 18 years old</p>
                      </div>

                      {/* WhatsApp Number */}
                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          WhatsApp Number
                        </label>
                        <input
                          type="tel"
                          value={kycFormData.whatsapp}
                          onChange={(e) => handleKycFormChange('whatsapp', e.target.value)}
                          placeholder="+91 9876543210"
                          className={`w-full border-2 rounded-lg px-4 py-2 focus:outline-none ${
                            kycFormErrors.whatsapp 
                              ? 'border-red-300 focus:border-red-500' 
                              : 'border-gray-300 focus:border-blue-500'
                          }`}
                        />
                        {kycFormErrors.whatsapp && (
                          <p className="text-red-500 text-xs mt-1">{kycFormErrors.whatsapp}</p>
                        )}
                        <p className="text-gray-500 text-xs mt-1">Optional: We'll use this for important updates</p>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="flex justify-end gap-3 pt-4">
                      <button
                        type="button"
                        onClick={() => setShowKycForm(false)}
                        className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={kycSubmitting}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {kycSubmitting ? (
                          <>
                            <Loader2 size={18} className="animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            <Check size={18} />
                            Submit KYC
                          </>
                        )}
                      </button>
                    </div>
                  </form>

                  {/* Disclaimer */}
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-blue-700 text-xs">
                      <strong>Note:</strong> By submitting your KYC details, you confirm that the information provided is accurate 
                      and matches your government-issued documents. This information is required as per SEBI regulations.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Account Security Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <Lock size={24} className="text-blue-600" />
          Account Security
        </h2>
        
        <div className="grid md:grid-cols-2 gap-6">
          {/* Change Password */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-semibold text-gray-900">Change Password</h3>
                <p className="text-sm text-gray-500">Update your account password</p>
              </div>
              <button
                onClick={() => setShowPasswordForm(!showPasswordForm)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Lock size={16} />
                {showPasswordForm ? 'Cancel' : 'Change'}
              </button>
            </div>
            
            {showPasswordForm && (
              <form onSubmit={handlePasswordChange} className="mt-4 space-y-4 border-t pt-4">
                {/* Current Password */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Current Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.old ? 'text' : 'password'}
                      value={passwordData.old_password}
                      onChange={(e) => {
                        setPasswordData(prev => ({ ...prev, old_password: e.target.value }));
                        if (passwordErrors.old_password) {
                          setPasswordErrors(prev => ({ ...prev, old_password: null }));
                        }
                      }}
                      className={`w-full border-2 rounded-lg px-4 py-2 pr-10 focus:outline-none ${
                        passwordErrors.old_password 
                          ? 'border-red-300 focus:border-red-500' 
                          : 'border-gray-300 focus:border-blue-500'
                      }`}
                      placeholder="Enter current password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(prev => ({ ...prev, old: !prev.old }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords.old ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {passwordErrors.old_password && (
                    <p className="text-red-500 text-xs mt-1">{passwordErrors.old_password}</p>
                  )}
                </div>
                
                {/* New Password */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    New Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.new ? 'text' : 'password'}
                      value={passwordData.new_password}
                      onChange={(e) => {
                        setPasswordData(prev => ({ ...prev, new_password: e.target.value }));
                        if (passwordErrors.new_password) {
                          setPasswordErrors(prev => ({ ...prev, new_password: null }));
                        }
                      }}
                      className={`w-full border-2 rounded-lg px-4 py-2 pr-10 focus:outline-none ${
                        passwordErrors.new_password 
                          ? 'border-red-300 focus:border-red-500' 
                          : 'border-gray-300 focus:border-blue-500'
                      }`}
                      placeholder="Enter new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {passwordErrors.new_password && (
                    <p className="text-red-500 text-xs mt-1">{passwordErrors.new_password}</p>
                  )}
                  <p className="text-gray-500 text-xs mt-1">Min 8 chars, with uppercase, lowercase & number</p>
                </div>
                
                {/* Confirm New Password */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Confirm New Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.confirm ? 'text' : 'password'}
                      value={passwordData.new_password_confirm}
                      onChange={(e) => {
                        setPasswordData(prev => ({ ...prev, new_password_confirm: e.target.value }));
                        if (passwordErrors.new_password_confirm) {
                          setPasswordErrors(prev => ({ ...prev, new_password_confirm: null }));
                        }
                      }}
                      className={`w-full border-2 rounded-lg px-4 py-2 pr-10 focus:outline-none ${
                        passwordErrors.new_password_confirm 
                          ? 'border-red-300 focus:border-red-500' 
                          : 'border-gray-300 focus:border-blue-500'
                      }`}
                      placeholder="Confirm new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {passwordErrors.new_password_confirm && (
                    <p className="text-red-500 text-xs mt-1">{passwordErrors.new_password_confirm}</p>
                  )}
                </div>
                
                <button
                  type="submit"
                  disabled={passwordChanging}
                  className="w-full py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {passwordChanging ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Changing Password...
                    </>
                  ) : (
                    <>
                      <Lock size={18} />
                      Update Password
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
          
          {/* Delete Account */}
          <div className="border border-red-200 rounded-lg p-4 bg-red-50">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-semibold text-red-700">Delete Account</h3>
                <p className="text-sm text-red-600">Permanently delete your account and all data</p>
              </div>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                <Trash2 size={16} />
                Delete
              </button>
            </div>
            <p className="text-xs text-red-600 mt-2">
              ⚠️ This action is irreversible. All your data, subscriptions, and history will be permanently deleted.
            </p>
          </div>
        </div>
      </div>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertCircle className="text-red-600" size={24} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Delete Account</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-700">
                  <strong>Warning:</strong> Deleting your account will:
                </p>
                <ul className="text-sm text-red-600 mt-2 space-y-1 list-disc list-inside">
                  <li>Remove all your personal data</li>
                  <li>Cancel any active subscriptions</li>
                  <li>Delete your stock watchlist</li>
                  <li>Remove access to all reports and features</li>
                </ul>
              </div>
              
              {/* Reason (optional) */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Reason for leaving (optional)
                </label>
                <textarea
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  rows={2}
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:border-red-500 resize-none"
                  placeholder="Help us improve by sharing why you're leaving..."
                />
              </div>
              
              {/* Password confirmation */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Enter your password to confirm <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:border-red-500"
                  placeholder="Enter your password"
                />
              </div>
              
              {/* Type DELETE confirmation */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Type <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">DELETE</span> to confirm
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:border-red-500 font-mono"
                  placeholder="Type DELETE"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmText('');
                  setDeletePassword('');
                  setDeleteReason('');
                }}
                className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleting || deleteConfirmText !== 'DELETE' || !deletePassword}
                className="flex-1 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={18} />
                    Delete Account
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
